import { createFileRoute, redirect } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { SlidersHorizontalIcon } from '@phosphor-icons/react';
import { ALLOWED_LLM_MODEL_IDS } from '@logspot-platform/platform-types';
import { AiService } from '@/api/ai-service';
import { aiSettingsOptions } from '@/hooks/query-options';
import { modelLabel } from '@/lib/data-credits';
import { useAppForm } from '@/hooks/use-app-form';
import { useFeatures } from '@/hooks/use-features';
import { PageHeader } from '@/components/page-header';
import { PageContent } from '@/components/page-content';
import { SectionHeader } from '@/components/section-header';
import { Card, CardContent } from '@/components/ui/card';
import { SelectItem } from '@/components/ui/select';
import { hasPermission } from '@/lib/permissions';

export const Route = createFileRoute('/_auth/_side-layout/settings_/ai')({
  component: AiPage,
  beforeLoad: async ({ context }) => {
    if (!hasPermission(context.userRole, { organization: ['update'] })) {
      throw redirect({ to: '/' });
    }
  },
});

const DESCRIPTION =
  'Configure AI model routing. AI-powered actions live under Actions.';

// Launch-gated on the master `ai` flag. AI-powered actions live under the
// Actions section, each gated on its own per-action flag.
function AiPage() {
  const { isEnabled } = useFeatures();

  return (
    <>
      <PageHeader title="AI" description={DESCRIPTION} isNew />
      <PageContent>
        {isEnabled('ai') ? (
          <AiSettingsForm />
        ) : (
          <p className="text-muted-foreground text-sm">
            AI features aren&apos;t enabled for your organization yet.
          </p>
        )}
      </PageContent>
    </>
  );
}

// Sentinel values for "no model pinned". TanStack Form's Select binds to a
// string; we map these to `null` / "inherit" (omit) on submit so the API
// receives the right shape.
const AUTO = '__auto__';
const INHERIT = '__inherit__';

type AiSettingsFormProps = {
  preferredModelId: string | null;
  perActionOverrides: Record<string, string>;
  aiActions: Array<{ key: string; label: string }>;
};

// Inner form — keyed by the loaded settings so defaults are stable; the parent
// only renders this once data resolves.
function SettingsForm({
  preferredModelId,
  perActionOverrides,
  aiActions,
}: AiSettingsFormProps) {
  const queryClient = useQueryClient();

  const save = useMutation({
    mutationFn: (input: {
      preferredModelId: string | null;
      perActionOverrides: Record<string, string>;
    }) => AiService.updateSettings(input),
    onSuccess: (settings) => {
      queryClient.setQueryData(['ai-settings'], settings);
      toast.success('AI settings saved');
    },
    onError: () => toast.error('Could not save AI settings'),
  });

  // Build flat default values: global model + one entry per action override.
  const defaultValues: Record<string, string> = {
    preferred: preferredModelId ?? AUTO,
  };
  for (const { key } of aiActions) {
    defaultValues[`override_${key}`] = perActionOverrides[key] ?? INHERIT;
  }

  const form = useAppForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      const overrides: Record<string, string> = {};
      for (const { key } of aiActions) {
        const picked = value[`override_${key}`];
        if (picked && picked !== INHERIT) overrides[key] = picked;
      }
      const preferred = value.preferred ?? AUTO;
      const settings = await save.mutateAsync({
        preferredModelId: preferred === AUTO ? null : preferred,
        perActionOverrides: overrides,
      });
      // Re-seed defaults from the saved result so dirty detection resets.
      const next: Record<string, string> = {
        preferred: settings.preferredModelId ?? AUTO,
      };
      for (const { key } of settings.aiActions) {
        next[`override_${key}`] = settings.perActionOverrides[key] ?? INHERIT;
      }
      form.reset(next);
    },
  });

  return (
    <form
      className="space-y-6"
      onSubmit={(e) => {
        e.preventDefault();
        form.handleSubmit();
      }}
    >
      <form.AppForm>
        <form.AppField name="preferred">
          {(field) => (
            <field.Select
              label="Preferred Model"
              description="Used for AI actions unless overridden below."
            >
              <SelectItem value={AUTO}>Auto (default)</SelectItem>
              {ALLOWED_LLM_MODEL_IDS.map((id) => (
                <SelectItem key={id} value={id}>
                  {modelLabel(id)}
                </SelectItem>
              ))}
            </field.Select>
          )}
        </form.AppField>

        {aiActions.length > 0 && (
          <div className="space-y-4">
            <h4 className="text-sm font-medium">Per-Action Overrides</h4>
            {aiActions.map(({ key, label }) => (
              <form.AppField key={key} name={`override_${key}`}>
                {(field) => (
                  <field.Select label={label}>
                    <SelectItem value={INHERIT}>
                      Inherit (preferred model)
                    </SelectItem>
                    {ALLOWED_LLM_MODEL_IDS.map((id) => (
                      <SelectItem key={id} value={id}>
                        {modelLabel(id)}
                      </SelectItem>
                    ))}
                  </field.Select>
                )}
              </form.AppField>
            ))}
          </div>
        )}

        <form.SubmitButton isPending={save.isPending} />
      </form.AppForm>
    </form>
  );
}

// AI Settings form (PRD 5.1). Global preferred model + per-action overrides.
// Gated `ai` by the parent page.
function AiSettingsForm() {
  const { data, isLoading } = useQuery(aiSettingsOptions());

  return (
    <Card>
      <CardContent className="space-y-5">
        <SectionHeader
          icon={<SlidersHorizontalIcon size={20} />}
          title="Model Routing"
          subTitle="Choose which model AI actions run on, globally or per action."
        />
        {isLoading || !data ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : (
          <SettingsForm
            preferredModelId={data.preferredModelId}
            perActionOverrides={data.perActionOverrides}
            aiActions={data.aiActions}
          />
        )}
      </CardContent>
    </Card>
  );
}
